import { useState, useEffect } from 'react';
import { useDateTimeFormat } from '../../hooks/useDateTimeFormat';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, 
  MapPin, 
  Clock, 
  User, 
  Users, 
  Briefcase, 
  Car, 
  Phone, 
  FileText, 
  Download,
  CheckCircle,
  XCircle,
  Navigation,
  UserCheck,
  Flag,
  Camera,
  Calendar,
  DollarSign,
  Plane,
  Building2,
  ChevronDown,
  ChevronUp,
  Plus,
  Upload,
  Loader2,
  Star,
  AlertTriangle,
  RefreshCw,
  Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';


export default function RideDetailModal({ ride, drivers, open, onClose, onRideUpdated }) {
  const { formatDateTime } = useDateTimeFormat();
  const { token, user } = useAuth();
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [isAddingPhotos, setIsAddingPhotos] = useState(false);
  const [retryingTzStatus, setRetryingTzStatus] = useState(false);

  const retryTransferzStatus = async () => {
    setRetryingTzStatus(true);
    try {
      await axios.post(`${API}/rides/${ride.id}/transferz-status-retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Nuovo tentativo avviato');
      if (onRideUpdated) onRideUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel riprovare');
    } finally {
      setRetryingTzStatus(false);
    }
  };
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  // Rating adjustment state
  const [showRatingSection, setShowRatingSection] = useState(false);
  const [ratingData, setRatingData] = useState(null);
  const [loadingRating, setLoadingRating] = useState(false);
  const [savingRating, setSavingRating] = useState(false);
  const [ratingAdjustments, setRatingAdjustments] = useState({
    punctuality_cause: 'driver_fault',
    reliability_cause: 'driver_fault',
    has_service_complaint: false,
    no_pickup_by_driver: false,
    arrived_at_pickup_driver_fault: true,
    picked_up_customer_driver_fault: true,
    arrived_at_dropoff_driver_fault: true,
    too_fast_driver_fault: true,
  });

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'admin-driver';

  // Flight delay state
  const [showFlightDelayForm, setShowFlightDelayForm] = useState(false);
  const [flightDelayTime, setFlightDelayTime] = useState('');
  const [flightDelayNotes, setFlightDelayNotes] = useState('');
  const [submittingFlightDelay, setSubmittingFlightDelay] = useState(false);

  // Driver No-Show: admin override (forces rating to 0, bypasses status flow)
  const [showDriverNoShowDialog, setShowDriverNoShowDialog] = useState(false);
  const [driverNoShowNotes, setDriverNoShowNotes] = useState('');
  const [submittingDriverNoShow, setSubmittingDriverNoShow] = useState(false);

  const handleReportFlightDelay = async () => {
    if (!flightDelayTime) {
      toast.error('Inserisci il nuovo orario di arrivo');
      return;
    }
    setSubmittingFlightDelay(true);
    try {
      // Combine ride date with the new time
      const rideDate = ride.date_time ? ride.date_time.split('T')[0] : new Date().toISOString().split('T')[0];
      const newArrival = `${rideDate}T${flightDelayTime}:00`;
      
      await axios.post(`${API}/rides/${ride.id}/flight-delay`, {
        new_arrival_time: newArrival,
        notes: flightDelayNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flight delay updated! Rating recalculated.');
      setShowFlightDelayForm(false);
      setFlightDelayTime('');
      setFlightDelayNotes('');
      if (onRideUpdated) onRideUpdated();
      // Reload rating if available
      if (ride.status === 'completed' || ride.status === 'no_show' || ride.status === 'driver_no_show') {
        loadRatingData();
      }
    } catch (error) {
      console.error('Error reporting flight delay:', error);
      toast.error(error.response?.data?.detail || 'Error reporting flight delay');
    } finally {
      setSubmittingFlightDelay(false);
    }
  };

  // Load rating data when modal opens for completed/no_show rides
  useEffect(() => {
    if (ride && (ride.status === 'completed' || ride.status === 'no_show' || ride.status === 'driver_no_show') && open) {
      loadRatingData();
      // Initialize adjustments from ride data
      setRatingAdjustments({
        punctuality_cause: ride.punctuality_cause || 'driver_fault',
        reliability_cause: ride.reliability_cause || 'driver_fault',
        has_service_complaint: ride.has_service_complaint || false,
        no_pickup_by_driver: ride.no_pickup_by_driver || false,
        arrived_at_pickup_driver_fault: ride.arrived_at_pickup_driver_fault !== false,
        picked_up_customer_driver_fault: ride.picked_up_customer_driver_fault !== false,
        arrived_at_dropoff_driver_fault: ride.arrived_at_dropoff_driver_fault !== false,
        too_fast_driver_fault: ride.too_fast_driver_fault !== false,
      });
    }
  }, [ride, open]);

  const loadRatingData = async () => {
    if (!ride?.id) return;
    setLoadingRating(true);
    try {
      const response = await axios.get(`${API}/rides/${ride.id}/rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingData(response.data);
    } catch (error) {
      console.error('Error loading rating:', error);
      // Rating might not exist yet
    } finally {
      setLoadingRating(false);
    }
  };

  const handleSaveRatingAdjustments = async () => {
    setSavingRating(true);
    try {
      const response = await axios.put(`${API}/rides/${ride.id}/rating`, ratingAdjustments, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingData(response.data.rating);
      toast.success('Rating updated successfully');
      if (onRideUpdated) {
        onRideUpdated();
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      toast.error('Error saving rating');
    } finally {
      setSavingRating(false);
    }
  };

  const handleRecalculateRating = async () => {
    setSavingRating(true);
    try {
      const response = await axios.post(`${API}/rides/${ride.id}/recalculate-rating`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingData(response.data.rating);
      toast.success('Rating recalculated');
    } catch (error) {
      console.error('Error recalculating rating:', error);
      toast.error('Error recalculating');
    } finally {
      setSavingRating(false);
    }
  };

  // Get driver name
  const driver = ride ? drivers?.find(d => d.id === ride.assigned_driver_id) : null;
  const driverName = driver?.name || 'Unassigned';

  // Format timestamp for timeline
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    };
  };

  // Build timeline events
  // Get no-show photos - load from dedicated endpoint for disk-based photos
  const [noShowPhotosLoaded, setNoShowPhotosLoaded] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  
  useEffect(() => {
    if (open && ride && ride.status === 'no_show') {
      const loadPhotos = async () => {
        setLoadingPhotos(true);
        try {
          const response = await axios.get(`${API}/rides/${ride.id}/no-show-photos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setNoShowPhotosLoaded(response.data);
        } catch (error) {
          console.error('Error loading no-show photos:', error);
          const fallback = ride.no_show_proof_photos || [];
          setNoShowPhotosLoaded(fallback);
        } finally {
          setLoadingPhotos(false);
        }
      };
      loadPhotos();
    }
  }, [open, ride?.id, ride?.status, token]);
  
  const allPhotos = noShowPhotosLoaded;

  // === ALL HOOKS ARE ABOVE THIS LINE ===
  // Safe to use conditional returns and ride access below

  if (!ride) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Loading...</DialogTitle></DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const buildTimeline = () => {
    const events = [];
    
    // Ride created
    if (ride.created_at) {
      events.push({
        action: 'Ride Created',
        timestamp: ride.created_at,
        icon: Calendar,
        color: 'bg-slate-500'
      });
    }

    // Ride assigned (if different from creation)
    if (ride.assigned_driver_id && ride.updated_at && ride.updated_at !== ride.created_at) {
      // We don't have exact assignment time, skip this
    }

    // Status changed to in_progress (driver started navigation)
    if (ride.status === 'in_progress' || ride.arrived_at_pickup || ride.picked_up_customer || ride.arrived_at_dropoff || ride.status === 'completed') {
      // We infer this happened but don't have exact timestamp unless arrived_at_pickup exists
    }

    // Arrived at pickup
    if (ride.arrived_at_pickup) {
      events.push({
        action: 'Arrived at Pickup',
        timestamp: ride.arrived_at_pickup,
        icon: MapPin,
        color: 'bg-blue-500',
        description: 'Driver clicked "I\'m at the Pickup Point"'
      });
    }

    // Picked up customer
    if (ride.picked_up_customer) {
      events.push({
        action: 'Passenger Picked Up',
        timestamp: ride.picked_up_customer,
        icon: UserCheck,
        color: 'bg-emerald-500',
        description: 'Driver clicked "Passenger Picked Up"'
      });
    }

    // Arrived at dropoff / Completed
    if (ride.arrived_at_dropoff) {
      events.push({
        action: 'Ride Completed',
        timestamp: ride.arrived_at_dropoff,
        icon: Flag,
        color: 'bg-emerald-600',
        description: 'Driver clicked "Complete Ride"'
      });
    }

    // No Show
    if (ride.no_show_marked_at) {
      events.push({
        action: 'Marked as No Show',
        timestamp: ride.no_show_marked_at,
        icon: XCircle,
        color: 'bg-red-500',
        description: ride.no_show_notes || 'Passenger did not show up'
      });
    }

    // Sort by timestamp
    events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    return events;
  };

  const timeline = buildTimeline();

  // Download photo function
  const downloadPhoto = (photo, index) => {
    if (!photo.data) {
      toast.error('Photo data not available');
      return;
    }
    const link = document.createElement('a');
    link.href = photo.data;
    link.download = photo.filename || `no_show_proof_${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download all photos as a single ZIP archive (server-side packaging)
  const downloadAllPhotos = async () => {
    if (!ride?.id) return;
    try {
      const response = await axios.get(`${API}/rides/${ride.id}/no-show-photos/download-zip`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `NoShow_Proofs_${ride.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Proof photos downloaded as ZIP');
    } catch (err) {
      console.error('Download ZIP error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download photos');
    }
  };

  // Admin photo upload handlers
  // Compress image before upload
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleNewPhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const currentTotal = allPhotos.length + newPhotos.length;
    if (currentTotal + files.length > 15) {
      toast.error(`Maximum 15 photos allowed. You can add ${15 - currentTotal} more.`);
      return;
    }
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }
      
      const compressed = await compressImage(file);
      setNewPhotos(prev => [...prev, {
        id: Date.now() + Math.random(),
        data: compressed,
        filename: file.name.replace(/\.\w+$/, '.jpg'),
        preview: compressed
      }]);
    }
    
    e.target.value = '';
  };

  const removeNewPhoto = (photoId) => {
    setNewPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleUploadNewPhotos = async () => {
    if (newPhotos.length === 0) {
      toast.error('Please select photos to upload');
      return;
    }
    
    setUploadingPhotos(true);
    try {
      // Upload each new photo individually via multipart (more reliable)
      let uploadedCount = 0;
      for (const photo of newPhotos) {
        try {
          const base64Data = photo.data.split(',')[1] || photo.data;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'image/jpeg' });
          
          const formData = new FormData();
          formData.append('file', blob, photo.filename);
          
          await axios.post(`${API}/rides/${ride.id}/no-show-photo-upload`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 30000
          });
          uploadedCount++;
        } catch (photoErr) {
          console.error(`Error uploading photo ${photo.filename}:`, photoErr);
        }
      }
      
      if (uploadedCount > 0) {
        toast.success(`${uploadedCount} photo(s) added successfully`);
      } else {
        toast.error('Failed to upload photos');
      }
      
      setNewPhotos([]);
      setIsAddingPhotos(false);
      
      // Notify parent to refresh ride data
      if (onRideUpdated) {
        onRideUpdated();
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photos. Check your connection and try again.');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const cancelAddPhotos = () => {
    setNewPhotos([]);
    setIsAddingPhotos(false);
  };

  // Status badge
  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-amber-100 text-amber-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-slate-100 text-slate-800',
      no_show: 'bg-red-100 text-red-800',
      driver_no_show: 'bg-red-200 text-red-900 border border-red-400',
      paid_cancelled: 'bg-orange-100 text-orange-800',
      free_cancelled: 'bg-slate-100 text-slate-800'
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const formatStatus = (status) => {
    const labels = {
      paid_cancelled: 'PAID CANCELLED',
      free_cancelled: 'FREE CANCELLED',
      driver_no_show: 'DRIVER NO-SHOW'
    };
    return labels[status] || status?.replace('_', ' ').toUpperCase();
  };

  // Driver No-Show: admin override (forces rating to 0, bypasses status flow)
  const handleConfirmDriverNoShow = async () => {
    setSubmittingDriverNoShow(true);
    try {
      await axios.post(
        `${API}/rides/${ride.id}/driver-no-show`,
        { notes: driverNoShowNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ride marked as Driver No-Show. Rating set to 0.');
      setShowDriverNoShowDialog(false);
      setDriverNoShowNotes('');
      onClose();
      onRideUpdated && onRideUpdated();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to mark Driver No-Show');
    } finally {
      setSubmittingDriverNoShow(false);
    }
  };

  const handleUndoDriverNoShow = async () => {
    if (!window.confirm('Undo Driver No-Show? Status will be reset to active and rating cleared.')) return;
    try {
      await axios.post(
        `${API}/rides/${ride.id}/driver-no-show/undo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver No-Show undone');
      onClose();
      onRideUpdated && onRideUpdated();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to undo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Ride Details</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(ride.status)}`}>
              {formatStatus(ride.status)}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Transferz status push failure alert */}
        {ride.transferz_status_alert && (
          <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 flex items-start gap-2 text-sm text-orange-800" data-testid="transferz-status-alert">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">{ride.transferz_status_alert.message}</p>
              {ride.transferz_status_alert.detail && <p className="text-xs">{ride.transferz_status_alert.detail}</p>}
            </div>
            {['driver-underway', 'driver-arrived', 'in-progress', 'complete', 'no-show-action', 'no-show-photos'].includes(ride.transferz_status_alert.status) && (
              <Button size="sm" disabled={retryingTzStatus} onClick={retryTransferzStatus} className="bg-orange-600 hover:bg-orange-700 text-white flex-shrink-0" data-testid="transferz-status-retry-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${retryingTzStatus ? 'animate-spin' : ''}`} /> Riprova
              </Button>
            )}
          </div>
        )}

        {/* Admin actions: Driver No-Show override (forces rating to 0) */}
        {isAdmin && (ride.status === 'active' || ride.status === 'in_progress' || ride.status === 'driver_no_show') && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-sm text-red-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Driver No-Show (admin override)</p>
                <p className="text-xs text-red-700">
                  {ride.status === 'driver_no_show'
                    ? `Marked by ${ride.driver_no_show_marked_by_name || 'admin'}. Rating forced to 0.`
                    : 'Use this if the driver did NOT show up. Rating will be set to 0.'}
                </p>
                {ride.driver_no_show_notes && (
                  <p className="text-xs text-red-800 mt-1 italic">"{ride.driver_no_show_notes}"</p>
                )}
              </div>
            </div>
            {ride.status === 'driver_no_show' ? (
              <Button
                size="sm"
                variant="outline"
                onClick={handleUndoDriverNoShow}
                className="text-xs h-8 border-red-300 text-red-700 hover:bg-red-100"
                data-testid="undo-driver-no-show-btn"
              >
                Undo
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setShowDriverNoShowDialog(true)}
                className="text-xs h-8 bg-red-600 hover:bg-red-700 text-white"
                data-testid="mark-driver-no-show-btn"
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                Mark Driver No-Show
              </Button>
            )}
          </div>
        )}

        <div className="space-y-6">
          {/* Customer & Basic Info */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <User className="h-4 w-4" />
              Customer Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Name</p>
                <p className="font-medium">{ride.customer_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-slate-500">Phone</p>
                <p className="font-medium flex items-center gap-1">
                  {ride.customer_phone ? (
                    <a href={`tel:${ride.customer_phone}`} className="text-blue-600 hover:underline">
                      {ride.customer_phone}
                    </a>
                  ) : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Passengers</p>
                <p className="font-medium flex items-center gap-1">
                  <Users className="h-4 w-4 text-slate-400" />
                  {ride.number_of_people || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Luggage</p>
                <p className="font-medium flex items-center gap-1">
                  <Briefcase className="h-4 w-4 text-slate-400" />
                  {ride.luggage_count || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Route Info */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Navigation className="h-4 w-4" />
              Route Details
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Pickup</p>
                  <p className="font-medium">{ride.pickup_location || 'N/A'}</p>
                </div>
              </div>
              <div className="ml-4 border-l-2 border-dashed border-slate-300 h-4"></div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Dropoff</p>
                  <p className="font-medium">{ride.dropoff_location || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ride Details Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Clock className="h-3 w-3" /> Scheduled Time
              </p>
              <p className="font-medium text-sm mt-1">
                {ride.date_time ? formatDateTime(ride.date_time) : 'N/A'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <Car className="h-3 w-3" /> Vehicle
              </p>
              <p className="font-medium text-sm mt-1">{ride.vehicle_type || 'N/A'}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                <UserCheck className="h-3 w-3" /> Driver
              </p>
              <p className="font-medium text-sm mt-1">{driverName}</p>
            </div>
            {ride.flight_number && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                  <Plane className="h-3 w-3" /> Flight
                </p>
                <p className="font-medium text-sm mt-1">{ride.flight_number}</p>
              </div>
            )}

            {/* Flight Delay Info - always visible if reported */}
            {ride.flight_delay_info && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 col-span-2" data-testid="flight-delay-info">
                <p className="text-xs text-amber-700 uppercase flex items-center gap-1 font-semibold">
                  <AlertTriangle className="h-3 w-3" /> Flight Delay Reported
                </p>
                <div className="mt-1.5 space-y-1">
                  <p className="text-sm font-medium text-amber-900">
                    Nuovo arrivo: {ride.flight_delay_info.new_arrival_time ? 
                      (() => {
                        try { return new Date(ride.flight_delay_info.new_arrival_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }); }
                        catch { return ride.flight_delay_info.new_arrival_time; }
                      })() : 'N/A'}
                  </p>
                  {ride.flight_delay_info.notes && (
                    <p className="text-xs text-amber-700">Note: {ride.flight_delay_info.notes}</p>
                  )}
                  <p className="text-xs text-amber-600">
                    Reported by: {ride.flight_delay_info.reported_by_name || 'Unknown'} 
                    ({ride.flight_delay_info.reported_by_role || ''})
                    {ride.flight_delay_info.reported_at && ` - ${new Date(ride.flight_delay_info.reported_at).toLocaleString('it-IT')}`}
                  </p>
                </div>
              </div>
            )}

            {/* Admin: Report/Update Flight Delay */}
            {isAdmin && (ride.is_airport_pickup || ride.flight_number) && (
              <div className="col-span-2">
                {!showFlightDelayForm ? (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="admin-report-flight-delay-btn"
                    onClick={() => {
                      setShowFlightDelayForm(true);
                      // Pre-fill with existing time if available
                      if (ride.flight_delay_info?.new_arrival_time) {
                        try {
                          const dt = new Date(ride.flight_delay_info.new_arrival_time);
                          setFlightDelayTime(`${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`);
                        } catch { }
                      }
                    }}
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Plane className="h-4 w-4 mr-2" />
                    {ride.flight_delay_info ? 'Update Flight Time' : 'Report Flight Delay'}
                  </Button>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-semibold text-amber-700 uppercase">
                      {ride.flight_delay_info ? 'Update Arrival Time' : 'Report Flight Delay'}
                    </p>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="text-xs text-slate-600 block mb-1">Nuovo orario arrivo</label>
                        <input
                          type="time"
                          value={flightDelayTime}
                          onChange={(e) => setFlightDelayTime(e.target.value)}
                          className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                          data-testid="admin-flight-delay-time"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-slate-600 block mb-1">Notes (optional)</label>
                        <input
                          type="text"
                          value={flightDelayNotes}
                          onChange={(e) => setFlightDelayNotes(e.target.value)}
                          placeholder="E.g: Airline delay"
                          className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleReportFlightDelay}
                        disabled={submittingFlightDelay || !flightDelayTime}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="admin-confirm-flight-delay-btn"
                      >
                        {submittingFlightDelay ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShowFlightDelayForm(false); setFlightDelayTime(''); setFlightDelayNotes(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {ride.partner_name && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Partner
                </p>
                <p className="font-medium text-sm mt-1">{ride.partner_name}</p>
              </div>
            )}
            {(ride.price || ride.price === 0) && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500 uppercase flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Price
                </p>
                <p className="font-medium text-sm mt-1">€{ride.price}</p>
              </div>
            )}
            {(ride.driver_commission || ride.driver_commission === 0) && (
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500 uppercase">Driver Commission</p>
                <p className="font-medium text-sm mt-1">€{ride.driver_commission}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {ride.notes && (
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4" />
                Notes
              </h3>
              <p className="text-sm text-slate-700">{ride.notes}</p>
            </div>
          )}

          {/* Vouchers Section */}
          {ride.vouchers && ride.vouchers.length > 0 && (
            <div className="bg-purple-50 rounded-xl p-4">
              <h3 className="font-semibold text-purple-900 flex items-center gap-2 mb-4">
                <FileText className="h-4 w-4" />
                Vouchers ({ride.vouchers.length})
              </h3>
              <div className="space-y-2">
                {ride.vouchers.map((voucher, index) => (
                  <div 
                    key={voucher.id || index} 
                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-purple-200"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-purple-500" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{voucher.filename}</p>
                        {voucher.uploaded_by && (
                          <p className="text-xs text-slate-500">Uploaded by {voucher.uploaded_by}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/rides/${ride.id}/vouchers/${voucher.id}/download`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = voucher.filename || 'voucher';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success('Voucher downloaded');
                          } else {
                            toast.error('Failed to download voucher');
                          }
                        } catch (error) {
                          console.error('Download error:', error);
                          toast.error('Failed to download voucher');
                        }
                      }}
                      className="text-purple-700 border-purple-300 hover:bg-purple-100"
                      data-testid={`download-voucher-${voucher.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Port Files Section */}
          {ride.port_files && ride.port_files.length > 0 && (
            <div className="bg-cyan-50 rounded-xl p-4">
              <h3 className="font-semibold text-cyan-900 flex items-center gap-2 mb-4">
                <FileText className="h-4 w-4" />
                Port ({ride.port_files.length})
              </h3>
              <div className="space-y-2">
                {ride.port_files.map((portFile, index) => (
                  <div 
                    key={portFile.id || index} 
                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-cyan-200"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-cyan-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{portFile.filename}</p>
                        {portFile.uploaded_by && (
                          <p className="text-xs text-slate-500">Uploaded by {portFile.uploaded_by}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/rides/${ride.id}/port-files/${portFile.id}/download`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = portFile.filename || 'port_file';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success('Port file downloaded');
                          } else {
                            toast.error('Failed to download port file');
                          }
                        } catch (error) {
                          console.error('Download error:', error);
                          toast.error('Failed to download port file');
                        }
                      }}
                      className="text-cyan-700 border-cyan-300 hover:bg-cyan-100"
                      data-testid={`download-port-file-${portFile.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Driver Actions Timeline */}
          {timeline.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <Clock className="h-4 w-4" />
                Driver Actions Timeline
              </h3>
              <div className="space-y-4">
                {timeline.map((event, index) => {
                  const ts = formatTimestamp(event.timestamp);
                  const Icon = event.icon;
                  return (
                    <div key={index} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 ${event.color} rounded-full flex items-center justify-center`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        {index < timeline.length - 1 && (
                          <div className="w-0.5 h-full bg-slate-200 mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="font-medium text-slate-900">{event.action}</p>
                        {ts && (
                          <p className="text-sm text-slate-500">
                            {ts.date} at <span className="font-mono font-medium text-slate-700">{ts.time}</span>
                          </p>
                        )}
                        {event.description && (
                          <p className="text-sm text-slate-600 mt-1">{event.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Show Proof Photos - Show for no_show rides or if photos exist */}
          {(ride.status === 'no_show' || allPhotos.length > 0) && (
            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-red-900 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  No Show Proof Photos ({allPhotos.length}/15)
                </h3>
                <div className="flex items-center gap-2">
                  {allPhotos.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadAllPhotos}
                      className="text-red-700 border-red-300 hover:bg-red-100"
                      data-testid="download-all-no-show-photos-btn"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download ZIP ({allPhotos.length})
                    </Button>
                  )}
                  {allPhotos.length < 15 && !isAddingPhotos && (
                    <Button
                      size="sm"
                      onClick={() => setIsAddingPhotos(true)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Photos
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Add New Photos UI */}
              {isAddingPhotos && (
                <div className="mb-4 p-4 bg-white rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-slate-700 mb-3">
                    Add new proof photos ({newPhotos.length} selected, max {15 - allPhotos.length} more)
                  </p>
                  
                  {/* New Photos Preview */}
                  {newPhotos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {newPhotos.map((photo) => (
                        <div key={photo.id} className="relative aspect-square">
                          <img
                            src={photo.preview}
                            alt={photo.filename}
                            className="w-full h-full object-cover rounded-lg border border-slate-200"
                          />
                          <button
                            onClick={() => removeNewPhoto(photo.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Input */}
                  {allPhotos.length + newPhotos.length < 15 && (
                    <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors mb-3">
                      <div className="flex flex-col items-center">
                        <Camera className="h-6 w-6 text-slate-400 mb-1" />
                        <p className="text-sm text-slate-500">Click to select photos</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleNewPhotoSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelAddPhotos}
                      disabled={uploadingPhotos}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUploadNewPhotos}
                      disabled={newPhotos.length === 0 || uploadingPhotos}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      {uploadingPhotos ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Upload className="h-4 w-4" />
                          Upload {newPhotos.length} Photo{newPhotos.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Existing Photo Grid */}
              {allPhotos.length > 0 && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {(showAllPhotos ? allPhotos : allPhotos.slice(0, 6)).map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.data}
                          alt={`Proof ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-red-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(photo.data, '_blank')}
                        />
                        <button
                          onClick={() => downloadPhoto(photo, index)}
                          className="absolute bottom-2 right-2 w-9 h-9 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-colors"
                          title="Download this photo"
                          data-testid={`download-no-show-photo-${index}`}
                        >
                          <Download className="h-4 w-4 text-slate-700" />
                        </button>
                        <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                          {index + 1}/{allPhotos.length}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Show More/Less Button */}
                  {allPhotos.length > 6 && (
                    <button
                      onClick={() => setShowAllPhotos(!showAllPhotos)}
                      className="w-full mt-3 py-2 text-sm text-red-700 hover:text-red-900 flex items-center justify-center gap-1"
                    >
                      {showAllPhotos ? (
                        <>
                          <ChevronUp className="h-4 w-4" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Show {allPhotos.length - 6} More Photos
                        </>
                      )}
                    </button>
                  )}
                </>
              )}

              {/* No photos yet message */}
              {allPhotos.length === 0 && !isAddingPhotos && (
                <p className="text-sm text-red-600 text-center py-4">
                  No proof photos uploaded yet. Click "Add Photos" to upload.
                </p>
              )}

              {/* No Show Notes */}
              {ride.no_show_notes && (
                <div className="mt-4 pt-4 border-t border-red-200">
                  <p className="text-xs text-red-700 uppercase mb-1">Driver Notes</p>
                  <p className="text-sm text-red-900">{ride.no_show_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Driver Rating Section - Admin Only for Completed/No-Show Rides */}
          {isAdmin && (ride.status === 'completed' || ride.status === 'no_show' || ride.status === 'driver_no_show') && (
            <div className="bg-amber-50 rounded-xl p-4">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowRatingSection(!showRatingSection)}
              >
                <h3 className="font-semibold text-amber-900 flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Ride Rating
                  {ratingData?.total_score !== undefined && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-sm font-bold ${
                      ratingData.total_score >= 80 ? 'bg-emerald-100 text-emerald-800' :
                      ratingData.total_score >= 60 ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {ratingData.total_score.toFixed(1)} / 100
                    </span>
                  )}
                </h3>
                <button className="text-amber-700 hover:text-amber-900">
                  {showRatingSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
              </div>

              {showRatingSection && (
                <div className="mt-4 space-y-4">
                  {loadingRating ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                    </div>
                  ) : (
                    <>
                      {/* Rating Breakdown Display */}
                      {ratingData && (
                        <div className="bg-white rounded-lg p-4 border border-amber-200">
                          <p className="text-sm font-semibold text-slate-700 mb-3">Rating Breakdown:</p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {/* Ride Management */}
                            <div className="bg-slate-50 p-2 rounded">
                              <p className="text-xs text-slate-500">Ride Management (35%)</p>
                              <p className="font-bold">{ratingData.ride_management?.score || 0}</p>
                              {ratingData.ride_management?.errors?.length > 0 && (
                                <p className="text-xs text-red-600 mt-1">
                                  {ratingData.ride_management.errors.join(', ')}
                                </p>
                              )}
                              {ratingData.ride_management?.admin_performed_actions?.length > 0 && (
                                <div className="mt-1 space-y-0.5" data-testid="admin-performed-actions">
                                  {ratingData.ride_management.admin_performed_actions.map((a, i) => (
                                    <p key={i} className="text-[10px] text-orange-600 font-medium">
                                      ⚠ {a.action}: da {a.by}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Reliability */}
                            <div className="bg-slate-50 p-2 rounded">
                              <p className="text-xs text-slate-500">Reliability (25%)</p>
                              <p className="font-bold">{ratingData.reliability?.score || 0}</p>
                              {ratingData.reliability?.details && (
                                <p className="text-xs text-slate-600 mt-1">{ratingData.reliability.details}</p>
                              )}
                            </div>
                            
                            {/* Service Quality */}
                            <div className="bg-slate-50 p-2 rounded">
                              <p className="text-xs text-slate-500">Service Quality (5%)</p>
                              <p className="font-bold">{ratingData.service_quality?.score || 0}</p>
                              {ratingData.service_quality?.has_complaint && (
                                <p className="text-xs text-red-600 mt-1">Complaint present</p>
                              )}
                            </div>
                            
                            {/* Punctuality */}
                            <div className="bg-slate-50 p-2 rounded">
                              <p className="text-xs text-slate-500">
                                Punctuality (35%)
                                {ratingData.punctuality?.excluded && <span className="text-red-500"> - ESCLUSO</span>}
                              </p>
                              <p className="font-bold">{ratingData.punctuality?.score || 0}</p>
                              {ratingData.punctuality?.delay_minutes > 0 && (
                                <p className="text-xs text-slate-600 mt-1">
                                  Delay: {ratingData.punctuality.delay_minutes} min
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Admin Adjustments */}
                      <div className="bg-white rounded-lg p-4 border border-amber-200">
                        <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          Modifiche Admin (sovrascrive penalità automatiche)
                        </p>
                        
                        <div className="space-y-4">
                          {/* Punctuality Cause */}
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Pickup Delay Cause
                            </label>
                            <select
                              value={ratingAdjustments.punctuality_cause}
                              onChange={(e) => setRatingAdjustments(prev => ({
                                ...prev,
                                punctuality_cause: e.target.value
                              }))}
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid="punctuality-cause-select"
                            >
                              <option value="driver_fault">Driver's Fault</option>
                              <option value="external_causes">Cause esterne (traffico, cliente in ritardo)</option>
                            </select>
                            <p className="text-xs text-slate-500 mt-1">
                              Se "cause esterne", il ritardo non penalizza il punteggio
                            </p>
                          </div>

                          {/* Reliability Cause */}
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Reliability Issue Cause
                            </label>
                            <select
                              value={ratingAdjustments.reliability_cause}
                              onChange={(e) => setRatingAdjustments(prev => ({
                                ...prev,
                                reliability_cause: e.target.value
                              }))}
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid="reliability-cause-select"
                            >
                              <option value="driver_fault">Driver's Fault</option>
                              <option value="external_causes">Cause esterne</option>
                            </select>
                          </div>

                          {/* Service Complaint Toggle */}
                          <div className="flex items-center justify-between">
                            <div>
                              <label className="block text-xs font-medium text-slate-600">
                                Service Quality Complaint
                              </label>
                              <p className="text-xs text-slate-500">
                                Penalizza il punteggio qualità (-50 punti)
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setRatingAdjustments(prev => ({
                                ...prev,
                                has_service_complaint: !prev.has_service_complaint
                              }))}
                              className={`relative w-12 h-6 rounded-full transition-colors ${
                                ratingAdjustments.has_service_complaint 
                                  ? 'bg-red-500' 
                                  : 'bg-slate-300'
                              }`}
                              data-testid="complaint-toggle"
                            >
                              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                ratingAdjustments.has_service_complaint ? 'translate-x-6' : ''
                              }`} />
                            </button>
                          </div>

                          {/* No Pickup by Driver Toggle */}
                          <div className="flex items-center justify-between">
                            <div>
                              <label className="block text-xs font-medium text-slate-600">
                                Driver No-Show (Pickup)
                              </label>
                              <p className="text-xs text-slate-500">
                                Azzera il punteggio affidabilità (0 punti)
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setRatingAdjustments(prev => ({
                                ...prev,
                                no_pickup_by_driver: !prev.no_pickup_by_driver
                              }))}
                              className={`relative w-12 h-6 rounded-full transition-colors ${
                                ratingAdjustments.no_pickup_by_driver 
                                  ? 'bg-red-500' 
                                  : 'bg-slate-300'
                              }`}
                              data-testid="no-pickup-toggle"
                            >
                              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                ratingAdjustments.no_pickup_by_driver ? 'translate-x-6' : ''
                              }`} />
                            </button>
                          </div>

                          {/* Too fast pickup→dropoff override */}
                          {ratingData?.ride_management?.too_fast && (
                            <div className="border-t border-amber-200 pt-3 mt-2" data-testid="too-fast-override">
                              <div className="flex items-center justify-between bg-amber-50 rounded-md p-2">
                                <div className="min-w-0 pr-2">
                                  <p className="text-xs font-medium text-slate-700">
                                    Pickup → dropoff troppo veloce ({ratingData.ride_management.too_fast.minutes} min)
                                  </p>
                                  <p className="text-[10px] text-slate-500">
                                    {ratingAdjustments.too_fast_driver_fault === false
                                      ? 'Non colpa del driver — nessuna penalità'
                                      : 'Colpa del driver — penalità −20 punti'}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setRatingAdjustments(prev => ({
                                    ...prev,
                                    too_fast_driver_fault: !prev.too_fast_driver_fault
                                  }))}
                                  className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${
                                    ratingAdjustments.too_fast_driver_fault !== false ? 'bg-red-500' : 'bg-emerald-500'
                                  }`}
                                  data-testid="too-fast-fault-toggle"
                                >
                                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                    ratingAdjustments.too_fast_driver_fault !== false ? '' : 'translate-x-6'
                                  }`} />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Per-action admin overrides (only shown if at least one action was admin-performed) */}
                          {ratingData?.ride_management?.admin_performed_actions?.length > 0 && (
                            <div className="border-t border-amber-200 pt-3 mt-2 space-y-3" data-testid="admin-action-overrides">
                              <p className="text-xs font-semibold text-amber-700">
                                Azioni premute dall'admin — colpa del driver?
                              </p>
                              {ratingData.ride_management.admin_performed_actions.map((a, i) => (
                                <div key={i} className="flex items-center justify-between bg-amber-50 rounded-md p-2">
                                  <div className="min-w-0 pr-2">
                                    <p className="text-xs font-medium text-slate-700 truncate">
                                      {a.action} <span className="text-slate-400">— da {a.by}</span>
                                    </p>
                                    <p className="text-[10px] text-slate-500">
                                      {ratingAdjustments[a.fault_field] === false
                                        ? 'Non colpa del driver — nessuna penalità'
                                        : 'Colpa del driver — penalità −25 punti'}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setRatingAdjustments(prev => ({
                                      ...prev,
                                      [a.fault_field]: !prev[a.fault_field]
                                    }))}
                                    className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${
                                      ratingAdjustments[a.fault_field] !== false ? 'bg-red-500' : 'bg-emerald-500'
                                    }`}
                                    data-testid={`admin-action-fault-${a.fault_field}`}
                                    title={ratingAdjustments[a.fault_field] !== false ? 'Colpa driver (toggle per disattivare)' : 'Non colpa driver'}
                                  >
                                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                      ratingAdjustments[a.fault_field] !== false ? '' : 'translate-x-6'
                                    }`} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={handleRecalculateRating}
                            disabled={savingRating}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            data-testid="recalculate-rating-btn"
                          >
                            <RefreshCw className={`h-4 w-4 ${savingRating ? 'animate-spin' : ''}`} />
                            Ricalcola
                          </button>
                          <button
                            onClick={handleSaveRatingAdjustments}
                            disabled={savingRating}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            data-testid="save-rating-btn"
                          >
                            {savingRating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Driver No-Show Confirm Dialog */}
      <Dialog open={showDriverNoShowDialog} onOpenChange={(open) => { if (!submittingDriverNoShow) setShowDriverNoShowDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Mark Driver No-Show
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-900">
              <p className="font-semibold mb-1">⚠ This action will:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                <li>Set ride status to <strong>DRIVER NO-SHOW</strong></li>
                <li>Force the rating to <strong>0</strong> (all categories)</li>
                <li>Notify the assigned driver</li>
                <li>Bypass the normal status flow (no GPS/buttons needed)</li>
              </ul>
              <p className="text-xs mt-2 italic">You can undo this from the same modal.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1 block">Reason / Notes (optional)</label>
              <textarea
                value={driverNoShowNotes}
                onChange={(e) => setDriverNoShowNotes(e.target.value)}
                placeholder="e.g. Driver didn't answer phone, never arrived to pickup..."
                className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-red-400"
                data-testid="driver-no-show-notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowDriverNoShowDialog(false); setDriverNoShowNotes(''); }}
              disabled={submittingDriverNoShow}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDriverNoShow}
              disabled={submittingDriverNoShow}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-driver-no-show-btn"
            >
              {submittingDriverNoShow ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
              Confirm Driver No-Show
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
